import { requireService, wrap } from '@repo/di-ez';
import { FilterAlt, SortDown, SortUp } from '@repo/go-icons';
import type {
  GridColumnResizeParams,
  GridFilterModel,
  GridPremiumSlotsComponent,
  GridRowSelectionModel,
} from '@repo/go-table';
import { Table, useGridApiRef } from '@repo/go-table';
import { Loader } from '@repo/go-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TheWellEntity } from 'src/services/wells-service/entities';
import { debounce } from 'src/shared/utils/debounce';
import { useStore } from 'src/store';

import { useGetColumnGrouping } from './hooks/use-get-column-grouping';
import { useGetTableColumns } from './hooks/use-get-table-columns';
import { WellsTableSettings } from './table-settings/table-settings';
import { getActualOrderModel } from './utils';
import { WellsTableTemplates } from './wells-table-templates';
import styles from './wells-table.module.scss';
import { WellsTableStore } from './wells-table.store';

const muiLanguageKeys: Record<string, 'ruRU' | 'enUS' | 'esES'> = {
  ru: 'ruRU',
  en: 'enUS',
  es: 'esES',
};

export const WellsTable = wrap(function WellsTable() {
  const apiRef = useGridApiRef();
  const tableApiRefForExport = useGridApiRef();

  const wellsService = requireService('wellsService');
  const wellsPageStore = requireService('wellsPageStore');
  const userSettingsService = requireService('userSettings');
  const { language, notifications, planVersionsService, editing, directories } = useStore();
  const [store] = useState(
    () =>
      new WellsTableStore(
        wellsPageStore,
        wellsService,
        planVersionsService,
        userSettingsService,
        notifications,
        apiRef,
        tableApiRefForExport,
        directories,
      ),
  );
  const { tableTemplatesStore } = wellsPageStore;
  const { displayManager } = store;

  const columns = useGetTableColumns(store);
  const columnGrouping = useGetColumnGrouping(store);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(({ colDef: { field }, width }: GridColumnResizeParams) => {
      if (!displayManager) {
        return;
      }

      const columns = apiRef.current?.getAllColumns() ?? [];
      const updatedColumns: Record<string, number> = {};

      columns.forEach((column) => {
        if (column.field === field) {
          updatedColumns[field] = width;
        } else if (displayManager.settings.columnsWidth && !displayManager.settings.columnsWidth[column.field]) {
          updatedColumns[column.field] = column.computedWidth;
        }
      });

      displayManager.onWidthChange(updatedColumns);
    }, 300),
    [displayManager],
  );

  const slots = useMemo(
    (): Partial<GridPremiumSlotsComponent> => ({
      columnFilteredIcon() {
        return (
          <div style={{ backgroundColor: 'var(--brand-1)', borderRadius: '4px', padding: '2px' }}>
            <FilterAlt style={{ color: 'white' }} />
          </div>
        );
      },
      columnSortedAscendingIcon() {
        return (
          <div style={{ backgroundColor: 'var(--brand-1)', borderRadius: '4px', padding: '2px' }}>
            <SortUp style={{ color: 'white' }} />
          </div>
        );
      },
      columnSortedDescendingIcon() {
        return (
          <div style={{ backgroundColor: 'var(--brand-1)', borderRadius: '4px', padding: '2px' }}>
            <SortDown style={{ color: 'white' }} />
          </div>
        );
      },
      columnUnsortedIcon() {
        return (
          <div style={{ backgroundColor: 'transparent', borderRadius: '4px', padding: '2px', opacity: 0.5 }}>
            <SortUp style={{ color: 'white' }} />
          </div>
        );
      },
    }),
    [],
  );

  const handleRowSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      store.setSelectedRows(model);
    },
    [store],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      if (model.quickFilterValues?.[0]) {
        const { startDate, endDate, isDrilledWellsChecked, isPlannedWellsChecked, isUnplannedWellsChecked } =
          JSON.parse(model.quickFilterValues[0]) as {
            startDate?: number;
            endDate?: number;
            isDrilledWellsChecked?: boolean;
            isPlannedWellsChecked?: boolean;
            isUnplannedWellsChecked?: boolean;
          };

        wellsPageStore.setStartDate(startDate ?? null);
        wellsPageStore.setEndDate(endDate ?? null);
        wellsPageStore.setIsDrilledWellsChecked(isDrilledWellsChecked ?? true);
        wellsPageStore.setIsPlannedWellsChecked(isPlannedWellsChecked ?? true);
        wellsPageStore.setIsUnplannedWellsChecked(isUnplannedWellsChecked ?? true);
      }
      displayManager?.onFilterModelChange(model);
    },
    [displayManager, wellsPageStore],
  );

  useEffect(() => {
    if (!displayManager || !displayManager.settings.columnsWidth) {
      return;
    }

    for (const column of apiRef.current.getAllColumns()) {
      if (displayManager.settings.columnsWidth[column.field] === column.width) {
        continue;
      }

      apiRef.current.setColumnWidth(column.field, displayManager.settings.columnsWidth[column.field]);
    }
  }, [apiRef, displayManager]);

  useEffect(() => {
    if (!displayManager || displayManager.settings.columnsOrderModel?.length) {
      return;
    }

    displayManager.onColumnsOrderModelChange(columns.map((c) => c.field));
  }, [columns, displayManager]);

  useEffect(() => {
    return store.init();
  }, [store]);

  const columnsOrderModel = displayManager?.columnsOrderModel?.filter(
    (c) => !c.endsWith('_total') && !c.startsWith('csByYear'),
  );

  const isActualOrderModel = useMemo(() => {
    return columns.every((c) => columnsOrderModel?.includes(c.field));
  }, [columns, columnsOrderModel]);

  const actualColumnsOrderModel = useMemo(() => {
    if (isActualOrderModel || !columnsOrderModel?.length) {
      return columnsOrderModel;
    }

    return getActualOrderModel(columnGrouping, columns, columnsOrderModel);
  }, [columnGrouping, columns, columnsOrderModel, isActualOrderModel]);

  useEffect(() => {
    if (!isActualOrderModel && actualColumnsOrderModel) {
      displayManager?.onColumnsOrderModelChange(actualColumnsOrderModel);
    }
  }, [actualColumnsOrderModel, displayManager, isActualOrderModel]);

  useEffect(() => {
    store.setDefaultColumnsOrderModel(columns.map((c) => c.field));
    tableTemplatesStore.setDefaultColumnsOrderModel(columns.map((c) => c.field));
  }, [columns, store, tableTemplatesStore]);

  useEffect(() => {
    if (displayManager) {
      displayManager.setColumnsGrouping(columnGrouping);
    }
  }, [columnGrouping, displayManager]);

  if (!displayManager) {
    return <Loader />;
  }

  return (
    <div className={styles.wrapper} style={{ position: 'relative' }}>
      <div
        className="hidden_service_table_for_export"
        // Необходимый костыль, чтобы экспорт по шаблонам не изменял текущее состояние таблицы
        style={{ width: 1, height: 1, overflow: 'hidden', position: 'absolute' }}
      >
        <Table<TheWellEntity>
          apiRef={tableApiRefForExport}
          rowHeight={1}
          columns={columns}
          columnGroupingModel={columnGrouping}
          rows={[]}
          getRowId={(row) => row.entityId}
          language={muiLanguageKeys[language.language]}
        />
      </div>

      <Table
        loading={!tableTemplatesStore.isTemplatesInitiated || store.isWellsUpdating}
        initialState={{
          sorting: {
            sortModel: displayManager.sortingModel,
          },
          filter: {
            filterModel: {
              ...(displayManager.settings.filterModel ?? {}),
              items: displayManager.settings.filterModel?.items ?? [],
              quickFilterExcludeHiddenColumns: false,
            },
          },
          columns: {
            columnVisibilityModel: displayManager.columnsVisibilityModel,
          },
          pinnedColumns: displayManager.pinnedColumns,
        }}
        columns={columns}
        rows={store.rows}
        apiRef={apiRef}
        getRowId={(row) => row.entityId}
        rowHeight={56}
        columnHeaderHeight={56}
        columnGroupingModel={columnGrouping}
        language={muiLanguageKeys[language.language]}
        columnVisibilityModel={displayManager.columnsVisibilityModel}
        columnsOrderModel={actualColumnsOrderModel}
        checkboxSelection={
          (editing.isEditing && wellsPageStore.isEditingAvailable) || wellsPageStore.isTableCheckboxesOn
        }
        slots={slots}
        onSortModelChange={displayManager.onSortChange}
        onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
        onColumnsOrderModelChange={displayManager.onColumnsOrderModelChange}
        onFilterModelChange={handleFilterModelChange}
        onPinnedColumnsChange={displayManager.onPinChange}
        onColumnWidthChange={handleWidthChange}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        cellSelection
        style={{
          '--DataGrid-rowBorderColor': 'red',
        }}
        sx={{
          '.MuiDataGrid-columnHeaderDraggableContainer': {
            padding: '8px 0',
          },
          '.MuiDataGrid-menuIcon': {
            height: 24,
          },
          '.MuiIconButton-root': {
            padding: '2px 2px !important',
          },
          '.MuiDataGrid-columnHeaderTitleContainer': {
            alignItems: 'flex-start',
          },
          '.MuiDataGrid-cell': {
            borderTop: '1px solid var(--go-table-border-right-color) !important',
          },
          '.MuiDataGrid-columnHeaderTitleContainerContent': {
            height: '100%',
          },
          '.MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'wrap',
            height: '100%',
          },
        }}
      />
      <WellsTableSettings tableApi={apiRef} displayManager={displayManager} columns={columns} groups={columnGrouping} />
      <WellsTableTemplates tableApi={apiRef} displayManager={displayManager} onExportTemplates={store.export} />
    </div>
  );
});
